import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import useDeviceType from '../../hooks/useDeviceType';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * Toast Types
 */
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  LOADING: 'loading'
};

/**
 * ToastItem Component
 * Individual toast with DaisyUI alert styling
 */
const ToastItem = ({ toast, onRemove }) => {
  const getAlertClass = () => {
    switch (toast.type) {
      case TOAST_TYPES.SUCCESS:
        return 'alert-success';
      case TOAST_TYPES.ERROR:
        return 'alert-error';
      case TOAST_TYPES.WARNING:
        return 'alert-warning';
      case TOAST_TYPES.INFO:
        return 'alert-info';
      case TOAST_TYPES.LOADING:
        return 'alert-info';
      default:
        return 'alert-info';
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case TOAST_TYPES.SUCCESS:
        return <CheckCircleIcon className="w-5 h-5 shrink-0" />;
      case TOAST_TYPES.ERROR:
        return <XCircleIcon className="w-5 h-5 shrink-0" />;
      case TOAST_TYPES.WARNING:
        return <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />;
      case TOAST_TYPES.INFO:
        return <InformationCircleIcon className="w-5 h-5 shrink-0" />;
      case TOAST_TYPES.LOADING:
        return <ClockIcon className="w-5 h-5 shrink-0 animate-spin" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 shrink-0" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={`
        alert ${getAlertClass()}
        ${toast.type === TOAST_TYPES.LOADING ? 'pointer-events-none' : ''}
      `}
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-info h-6 w-6 shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>

      <div>
        <h3 className="font-bold">
          {toast.title}
        </h3>
        <div className="text-xs">
          {toast.message}
        </div>
      </div>

      {toast.type !== TOAST_TYPES.LOADING && (
        <button
          onClick={() => onRemove(toast.id)}
          className="btn btn-ghost btn-xs"
          aria-label="Dismiss toast"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      {toast.details && (
        <div className="text-xs opacity-75 mt-1 font-mono">
          {toast.details}
        </div>
      )}
    </motion.div>
  );
};

/**
 * ToastContainer Component
 * Renders the toast container with responsive positioning
 */
const ToastContainer = ({ toasts, onRemove }) => {
  const deviceType = useDeviceType();

  return (
    <div className={`toast z-50 space-y-2 ${
      deviceType.isMobile
        ? 'toast-top toast-center w-full px-4 max-w-full top-20'
        : 'toast-top toast-end p-4 max-w-sm'
    }`}>
      <AnimatePresence>
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onRemove={onRemove}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

/**
 * ToastProvider
 * Manages toast state and renders ToastContainer
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastCounter = useRef(0);

  const addToast = useCallback((toastConfig) => {
    const id = ++toastCounter.current;
    
    const newToast = {
      id,
      type: TOAST_TYPES.INFO,
      duration: 5000,
      autoRemove: true,
      ...toastConfig
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-remove after duration (except for loading toasts)
    if (newToast.autoRemove && newToast.type !== TOAST_TYPES.LOADING) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const showSuccess = useCallback((title, message, duration = 5000, details) => {
    return addToast({
      type: TOAST_TYPES.SUCCESS,
      title,
      message,
      details,
      duration
    });
  }, [addToast]);

  const showError = useCallback((title, message, duration = 8000, details) => {
    return addToast({
      type: TOAST_TYPES.ERROR,
      title,
      message,
      details,
      duration
    });
  }, [addToast]);

  const showWarning = useCallback((title, message, duration = 6000, details) => {
    return addToast({
      type: TOAST_TYPES.WARNING,
      title,
      message,
      details,
      duration
    });
  }, [addToast]);

  const showInfo = useCallback((title, message, duration = 5000, details) => {
    return addToast({
      type: TOAST_TYPES.INFO,
      title,
      message,
      details,
      duration
    });
  }, [addToast]);

  const showLoading = useCallback((title, message) => {
    return addToast({
      type: TOAST_TYPES.LOADING,
      title,
      message,
      autoRemove: false // Loading toasts don't auto-remove
    });
  }, [addToast]);

  const updateToast = useCallback((id, updates) => {
    setToasts(prev => prev.map(toast => 
      toast.id === id ? { ...toast, ...updates } : toast
    ));
  }, []);

  const value = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    updateToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

/**
 * Quick toast hook for convenience
 */
export const useQuickToast = () => {
  const { showSuccess, showError, showWarning, showInfo, showLoading, updateToast } = useToast();
  
  return {
    success: showSuccess,
    error: showError,
    warning: showWarning,
    info: showInfo,
    loading: showLoading,
    update: updateToast
  };
};

export default ToastProvider;